import { createTheme } from '@mui/material/styles';
import { FONT_BODY, FONT_MONO, ThemeName, tokensFor } from './tokens';

// The palette the app was first built on. Legacy pages keep it outright; dark
// brand pages still lean on its defaults (white dialogs, purple controls) until
// each is moved onto the tokens, so it stays their MUI palette for now.
const legacyPalette = {
  primary: {
    main: '#9C27B0',
    light: '#BA68C8',
    dark: '#7B1FA2',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#6b7280',
    light: '#9ca3af',
    dark: '#374151',
    contrastText: '#ffffff',
  },
  text: {
    primary: '#1a1a1a',
    secondary: '#6b7280',
  },
};

/**
 * The MUI theme for brand pages. Built from the hex tokens rather than CSS
 * variables: MUI's alpha/darken helpers cannot parse `var(...)`.
 */
export const createAppTheme = (mode: ThemeName) => {
  const t = tokensFor(mode);
  const light = mode === 'light';
  // On light, yellow survives only as a fill; wherever MUI would draw the primary
  // colour as text, an outline or a thin mark, it gets the dark gold instead.
  const mark = t['accent-fg'];
  const isPrimary = (ownerState: { color?: string }) => (ownerState.color ?? 'primary') === 'primary';

  return createTheme({
    palette: light
      ? {
          mode: 'light',
          primary: { main: t.accent, contrastText: t['on-accent'] },
          secondary: { main: t['fg-2'], contrastText: t.surface },
          background: { default: t.page, paper: t.surface },
          text: { primary: t['fg-body'], secondary: t['fg-2'], disabled: t['fg-muted'] },
          divider: t.line,
          success: { main: t.success },
          error: { main: t.danger },
          warning: { main: t.warning },
          info: { main: t.info },
        }
      : legacyPalette,
    typography: {
      fontFamily: FONT_BODY,
      h1: { fontFamily: FONT_MONO },
      h2: { fontFamily: FONT_MONO },
      h3: { fontFamily: FONT_MONO },
      h4: { fontFamily: FONT_MONO },
      h5: { fontFamily: FONT_MONO },
      h6: { fontFamily: FONT_MONO },
      body1: { fontFamily: FONT_BODY },
      body2: { fontFamily: FONT_BODY },
    },
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: { fontFamily: FONT_MONO },
          body: { fontFamily: FONT_BODY },
        },
      },
      ...(light && {
        MuiButton: {
          styleOverrides: {
            text: ({ ownerState }) => (isPrimary(ownerState) ? { color: mark } : {}),
            outlined: ({ ownerState }) => (isPrimary(ownerState) ? { color: mark, borderColor: mark } : {}),
          },
        },
        MuiTab: { styleOverrides: { root: { '&.Mui-selected': { color: mark } } } },
        MuiTabs: { styleOverrides: { indicator: { backgroundColor: mark } } },
        MuiCheckbox: { styleOverrides: { root: { '&.Mui-checked': { color: mark } } } },
        MuiRadio: { styleOverrides: { root: { '&.Mui-checked': { color: mark } } } },
        MuiLink: { styleOverrides: { root: { color: mark } } },
        MuiCircularProgress: {
          styleOverrides: { root: ({ ownerState }) => (isPrimary(ownerState) ? { color: mark } : {}) },
        },
      }),
    },
  });
};

/**
 * The theme the pre-brand AI-app pages were built against (purple accent, light
 * surfaces). Kept as-is so those pages are unchanged until they are rebranded.
 */
export const legacyTheme = createTheme({
  palette: legacyPalette,

  typography: {
    fontFamily: '"Inter", sans-serif',
    h1: { fontFamily: '"Inter", sans-serif' },
    h2: { fontFamily: '"Inter", sans-serif' },
    h3: { fontFamily: '"Inter", sans-serif' },
    h4: { fontFamily: '"Inter", sans-serif' },
    h5: { fontFamily: '"Inter", sans-serif' },
    h6: { fontFamily: '"Inter", sans-serif' },
    body1: { fontFamily: '"Inter", sans-serif' },
    body2: { fontFamily: '"Inter", sans-serif' },
  },
});
